import numbro from 'numbro';

import { BytesUnitKind, BytesUnitOptions } from '../schema';
import { FormatValueOptions, UnitGroupConfig, UnitConfig } from './types';
import { hasDecimalPlaces, limitDecimalPlaces, SCIENTIFIC_NOTATION_FORMATTER, shouldAbbreviate } from './utils';

/**
 * We consider the units for bytes to be powers of 1000.
 * In other words:
 * 1 KB = 1000 bytes (1000^1 bytes)
 * 1 MB = 1,000,000 bytes (1000^2 bytes)
 * etc.
 */

const DEFAULT_DECIMAL_PLACES = 2;
const TINY_BYTES_THRESHOLD = 0.0001;

const BYTES_GROUP = 'Bytes';
export const BYTES_GROUP_CONFIG: UnitGroupConfig = {
  label: 'Bytes',
  decimal_places: true,
  abbreviate: true,
};
export const BYTES_UNIT_CONFIG: Readonly<Record<BytesUnitKind, UnitConfig>> = {
  Bytes: {
    group: BYTES_GROUP,
    label: 'Bytes (decimal)',
    description: 'KB, MB, GB, etc.',
  },
  BinaryBytes: {
    group: BYTES_GROUP,
    label: 'Bytes (binary)',
    description: 'KiB, MiB, GiB, etc.',
  },
};

type FormatBytesOptions = BytesUnitOptions & Pick<FormatValueOptions, 'trimZeros' | 'scientificForTinyValues'>;

export function formatBytes(
  bytes: number,
  { kind, abbreviate, decimal_places, trimZeros = true, scientificForTinyValues = false }: FormatBytesOptions
) {
  const isDecimalBytes = kind === 'Bytes';
  const isBinaryBytes = kind === 'BinaryBytes';
  const isVerySmallValue = Math.abs(bytes) < TINY_BYTES_THRESHOLD;

  if (scientificForTinyValues && bytes !== 0 && isVerySmallValue) {
    return `${SCIENTIFIC_NOTATION_FORMATTER.format(bytes)} bytes`;
  }

  // If `abbreviate: false`, use Intl.NumberFormat.
  // If value < 1000 (decimal) or 1024 (binary), use Intl.NumberFormat so that it says "bytes" instead of "B" (which can be confused with 1 billion)
  if (
    !shouldAbbreviate(abbreviate) ||
    (isDecimalBytes && Math.abs(bytes) < 1000) ||
    (isBinaryBytes && Math.abs(bytes) < 1024)
  ) {
    const formatterOptions: Intl.NumberFormatOptions = {
      style: 'unit',
      unit: 'byte',
      unitDisplay: 'long',
      useGrouping: true,
    };

    if (hasDecimalPlaces(decimal_places)) {
      formatterOptions.maximumFractionDigits = limitDecimalPlaces(decimal_places);
      if (!trimZeros) {
        formatterOptions.minimumFractionDigits = limitDecimalPlaces(decimal_places);
      }
    } else {
      if (Math.abs(bytes) < 1) {
        // For values < 1, allow up to 3 significant digits
        formatterOptions.maximumSignificantDigits = 3;
      } else {
        // For values >= 1, allow up to 2 decimal places.
        formatterOptions.maximumFractionDigits = DEFAULT_DECIMAL_PLACES;
      }
    }

    const formatter = Intl.NumberFormat('en-US', formatterOptions);
    return formatter.format(bytes);
  }

  // If `abbreviate: true`, use numbro.
  // numbro is able to add units like KB, MB, GB, etc. correctly.
  return numbro(bytes).format({
    output: 'byte',
    base: isDecimalBytes ? 'decimal' : 'binary',
    spaceSeparated: true,
    mantissa: hasDecimalPlaces(decimal_places) ? decimal_places : DEFAULT_DECIMAL_PLACES,
    // trimMantissa trims trailing 0s
    trimMantissa: trimZeros,
    // optionalMantissa excludes all the decimal places if they're all zeros
    optionalMantissa: trimZeros,
  });
}
